import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  listSeasonsMarketplaces,
  searchSeasonsAccountByTeam,
  type BarcodeSyncRecommendResult,
  type SeasonsAccountByTeamResult,
  type SeasonsMarketplaceOption,
} from '../api/vlListing.api';
import styles from './BarcodeSyncConfirmModal.module.css';

export type BarcodeSyncConfirmContext = {
  eventName?: string;
  venue?: string;
  league?: string;
  teamName?: string;
};

type Props = {
  open: boolean;
  loadingRecommend: boolean;
  confirming: boolean;
  recommendation: BarcodeSyncRecommendResult | null;
  error?: string | null;
  title?: string;
  /** When set, only these purchase account ids start selected (e.g. single-card Sync). */
  initialPurchaseIds?: string[] | null;
  onClose: () => void;
  onConfirm: (accountIds: string[]) => void | Promise<void>;
};

const NO_ACCOUNT_ID_MESSAGE = 'no account id found';
const MAX_SUGGESTIONS = 12;

/**
 * Confirm gate before barcode crawl.
 * Seasons = single-select; Purchase Managers = multi-select.
 * Team search is always shown so staff can look up a marketplace during sync.
 */
const BarcodeSyncConfirmModal: React.FC<Props> = ({
  open,
  loadingRecommend,
  confirming,
  recommendation,
  error,
  title = 'Sync Barcodes',
  initialPurchaseIds = null,
  onClose,
  onConfirm,
}) => {
  const [selectedSeasonsId, setSelectedSeasonsId] = useState<string | null>(null);
  const [selectedPurchaseIds, setSelectedPurchaseIds] = useState<string[]>([]);
  const [teamQuery, setTeamQuery] = useState('');
  const [searchingTeam, setSearchingTeam] = useState(false);
  const [teamSearchError, setTeamSearchError] = useState<string | null>(null);
  const [teamSearchResult, setTeamSearchResult] = useState<SeasonsAccountByTeamResult | null>(null);
  const [marketplaces, setMarketplaces] = useState<SeasonsMarketplaceOption[]>([]);
  const [marketplacesLoading, setMarketplacesLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!open || !recommendation) return;
    const seasons = recommendation.seasonsAccount;
    setSelectedSeasonsId(
      seasons?.recommended && seasons.accountId ? String(seasons.accountId) : null,
    );
    setTeamQuery('');
    setTeamSearchError(null);
    setTeamSearchResult(null);
    setShowSuggestions(false);
    if (initialPurchaseIds?.length) {
      const allowed = new Set(
        (recommendation.purchaseAccounts || []).map((p) => String(p.accountId)),
      );
      setSelectedPurchaseIds(
        initialPurchaseIds.map(String).filter((id) => allowed.has(id)),
      );
      return;
    }
    const purchaseIds = (recommendation.purchaseAccounts || [])
      .filter((p) => p.recommended && p.accountId)
      .map((p) => String(p.accountId));
    setSelectedPurchaseIds([...new Set(purchaseIds)]);
  }, [open, recommendation, initialPurchaseIds]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setMarketplacesLoading(true);
    void listSeasonsMarketplaces()
      .then((result) => {
        if (!cancelled) setMarketplaces(result.marketplaces || []);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setMarketplaces([]);
          setTeamSearchError(err?.message || 'Failed to load marketplaces');
        }
      })
      .finally(() => {
        if (!cancelled) setMarketplacesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const suggestions = useMemo(() => {
    const q = teamQuery.trim().toLowerCase();
    if (!q || !showSuggestions) return [];
    return marketplaces
      .filter((mp) => mp.teamName.toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [teamQuery, marketplaces, showSuggestions]);

  const searchedAccountId = String(teamSearchResult?.accountId || '').trim();
  const selectedIds = useMemo(() => {
    const ids = [...selectedPurchaseIds];
    const seasonsId = searchedAccountId || selectedSeasonsId;
    if (seasonsId) ids.unshift(seasonsId);
    return [...new Set(ids.filter(Boolean))];
  }, [selectedSeasonsId, selectedPurchaseIds, searchedAccountId]);

  if (!open) return null;

  const togglePurchase = (accountId: string) => {
    setSelectedPurchaseIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId],
    );
  };

  const resolveTeamAccount = async (
    teamName: string,
    marketplaceId?: string,
  ) => {
    const query = teamName.trim();
    if (!query) {
      setTeamSearchError('Enter a team name to search');
      setTeamSearchResult(null);
      return;
    }
    setSearchingTeam(true);
    setTeamSearchError(null);
    setTeamSearchResult(null);
    setShowSuggestions(false);
    try {
      const result = await searchSeasonsAccountByTeam(
        query,
        marketplaceId ? { marketplaceId } : undefined,
      );
      setTeamSearchResult(result);
      if (String(result.accountId || '').trim()) {
        setSelectedSeasonsId(null);
      } else {
        setTeamSearchError(result.error || NO_ACCOUNT_ID_MESSAGE);
      }
    } catch (err: any) {
      setTeamSearchError(err?.message || 'Failed to search Seasons account');
    } finally {
      setSearchingTeam(false);
    }
  };

  const onSelectSuggestion = (mp: SeasonsMarketplaceOption) => {
    setTeamQuery(mp.teamName);
    setShowSuggestions(false);
    void resolveTeamAccount(mp.teamName, mp.marketplaceId);
  };

  return (
    <div className={styles.overlay}>
      <div
        className={styles.content}
        role="dialog"
        aria-modal="true"
        aria-labelledby="barcode-sync-confirm-title"
      >
        <div className={styles.header}>
          <div>
            <h3 id="barcode-sync-confirm-title">{title}</h3>
            <p className={styles.subtitle}>
              Search a team or review recommended accounts, then Confirm. Crawl runs only for the
              accounts you confirm.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => !confirming && onClose()}
            aria-label="Close"
            disabled={confirming}
          >
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          {loadingRecommend && <p className={styles.muted}>Loading recommended accounts…</p>}
          {error && <p className={styles.error}>{error}</p>}

          {!loadingRecommend && recommendation && (recommendation.eventName || recommendation.primaryTeam) && (
            <div className={styles.context}>
              {recommendation.eventName && (
                <div>
                  <strong>Event:</strong> {recommendation.eventName}
                </div>
              )}
              {recommendation.primaryTeam && (
                <div>
                  <strong>Team:</strong> {recommendation.primaryTeam}
                  {recommendation.sheetPlatform ? (
                    <div className={styles.accountMeta}>{recommendation.sheetPlatform}</div>
                  ) : null}
                </div>
              )}
              {recommendation.venue && (
                <div>
                  <strong>Venue:</strong> {recommendation.venue}
                </div>
              )}
              {recommendation.reason && (
                <div className={styles.reason}>{recommendation.reason}</div>
              )}
            </div>
          )}

          <section className={styles.section}>
            <h4>Seasons account (single-select)</h4>
            <div className={styles.teamSearch}>
              <p className={styles.muted}>
                {recommendation?.seasonsAccount
                  ? 'Search another team to override the recommended Seasons account.'
                  : 'Type a team name to see marketplace suggestions.'}
              </p>
              <div className={styles.searchRow}>
                <div className={styles.suggestWrap}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    value={teamQuery}
                    placeholder={
                      marketplacesLoading
                        ? 'Loading team names…'
                        : 'Type a team name'
                    }
                    autoComplete="off"
                    disabled={confirming || searchingTeam || marketplacesLoading}
                    onChange={(e) => {
                      setTeamQuery(e.target.value);
                      setTeamSearchResult(null);
                      setTeamSearchError(null);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowSuggestions(false);
                        return;
                      }
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (suggestions[0]) {
                          onSelectSuggestion(suggestions[0]);
                          return;
                        }
                        void resolveTeamAccount(teamQuery);
                      }
                    }}
                  />
                  {suggestions.length > 0 && (
                    <ul className={styles.suggestList} role="listbox">
                      {suggestions.map((mp) => (
                        <li key={`${mp.marketplaceId}-${mp.teamName}`}>
                          <button
                            type="button"
                            className={styles.suggestItem}
                            disabled={confirming || searchingTeam}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onSelectSuggestion(mp)}
                          >
                            {mp.teamName}
                            {mp.marketplaceName &&
                            mp.marketplaceName !== mp.teamName
                              ? ` (${mp.marketplaceName})`
                              : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.searchBtn}
                  disabled={confirming || searchingTeam || !teamQuery.trim()}
                  onClick={() => void resolveTeamAccount(teamQuery)}
                >
                  {searchingTeam ? 'Searching…' : 'Search'}
                </button>
              </div>
              {teamSearchError && <p className={styles.error}>{teamSearchError}</p>}
              {teamSearchResult && (
                <div className={styles.searchResult}>
                  <div>
                    <strong>Marketplace name:</strong>{' '}
                    {teamSearchResult.marketplaceName || '—'}
                  </div>
                  <div>
                    <strong>Marketplace id:</strong>{' '}
                    {teamSearchResult.marketplaceId || '—'}
                  </div>
                  <div>
                    <strong>Account type:</strong>{' '}
                    {teamSearchResult.accountType || '—'}
                  </div>
                  <div>
                    <strong>Account id:</strong> {teamSearchResult.accountId || '—'}
                  </div>
                  <div>
                    <strong>Username:</strong> {teamSearchResult.username || '—'}
                  </div>
                </div>
              )}
            </div>
            {recommendation?.seasonsAccount && (
              <>
                <label className={styles.row}>
                  <input
                    type="radio"
                    name="seasonsAccount"
                    checked={
                      !searchedAccountId &&
                      selectedSeasonsId ===
                        String(recommendation.seasonsAccount.accountId)
                    }
                    onChange={() => {
                      setTeamQuery('');
                      setTeamSearchResult(null);
                      setTeamSearchError(null);
                      setSelectedSeasonsId(
                        String(recommendation.seasonsAccount!.accountId),
                      );
                    }}
                    disabled={confirming}
                  />
                  <span>
                    <strong>
                      {recommendation.seasonsAccount.teamName ||
                        recommendation.seasonsAccount.marketplaceName ||
                        'Seasons'}
                    </strong>
                    {recommendation.seasonsAccount.marketplaceName &&
                      recommendation.seasonsAccount.marketplaceName !==
                        recommendation.seasonsAccount.teamName && (
                        <div className={styles.accountMeta}>
                          {recommendation.seasonsAccount.marketplaceName}
                        </div>
                      )}
                  </span>
                </label>
                {!searchedAccountId && (
                  <div className={styles.searchResult}>
                    <div>
                      <strong>Marketplace name:</strong>{' '}
                      {recommendation.seasonsAccount.marketplaceName || '—'}
                    </div>
                    <div>
                      <strong>Marketplace id:</strong>{' '}
                      {recommendation.seasonsAccount.marketplaceId || '—'}
                    </div>
                    <div>
                      <strong>Account type:</strong>{' '}
                      {recommendation.sheetType === 'purchase' ||
                      recommendation.seasonsAccount.marketplaceName === 'Ticketmaster'
                        ? 'Purchase'
                        : 'Seasons'}
                    </div>
                    <div>
                      <strong>Account id:</strong>{' '}
                      {recommendation.seasonsAccount.accountId || '—'}
                    </div>
                    <div>
                      <strong>Username:</strong>{' '}
                      {recommendation.seasonsAccount.username || '—'}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className={styles.clearLink}
                  disabled={confirming || (!selectedSeasonsId && !searchedAccountId)}
                  onClick={() => {
                    setSelectedSeasonsId(null);
                    setTeamSearchResult(null);
                  }}
                >
                  Clear Seasons selection
                </button>
              </>
            )}
          </section>

          {!loadingRecommend && recommendation && (
            <>
              <section className={styles.section}>
                <h4>Purchase accounts (multi-select)</h4>
                {!recommendation.purchaseAccounts?.length ? (
                  <p className={styles.muted}>
                    No Purchase accounts configured in this VL env (check VL_CRAWL_ACCOUNTS).
                  </p>
                ) : (
                  recommendation.purchaseAccounts.map((p) => (
                    <label key={p.accountId} className={styles.row}>
                      <input
                        type="checkbox"
                        checked={selectedPurchaseIds.includes(String(p.accountId))}
                        onChange={() => togglePurchase(String(p.accountId))}
                        disabled={confirming}
                      />
                      <span>
                        <strong>{p.managerName || p.marketplaceName}</strong>
                        <div className={styles.accountMeta}>
                          <div>
                            <strong>Marketplace name:</strong> {p.marketplaceName || '—'}
                          </div>
                          <div>
                            <strong>Marketplace id:</strong> {p.marketplaceId || '—'}
                          </div>
                          <div>
                            <strong>Account id:</strong> {p.accountId || '—'}
                          </div>
                        </div>
                      </span>
                    </label>
                  ))
                )}
              </section>

              <p className={styles.hint}>
                {recommendation.rulesNote ||
                  'Seasons = single-select. Purchase = multi-select. Search a team, then Confirm.'}
              </p>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={confirming}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={
              confirming ||
              searchingTeam ||
              selectedIds.length === 0 ||
              (loadingRecommend && !searchedAccountId)
            }
            onClick={() => void onConfirm(selectedIds)}
            title={
              teamSearchResult && !searchedAccountId
                ? NO_ACCOUNT_ID_MESSAGE
                : undefined
            }
          >
            {confirming ? 'Syncing…' : `Confirm sync (${selectedIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeSyncConfirmModal;
